import { Box, xcss } from '@atlaskit/primitives';
import { type Participant } from '@databases/utils/presence/types';
import React, { type RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useDisplayParticipants } from '../hooks/useDisplayParticipants';

const NAMEPLATE_HEIGHT_IN_PIXELS = 24;
const BORDER_WIDTH_IN_PIXELS = 2;

const getNameplateStyles = ({ position = 'top' }: { position?: 'top' | 'bottom' }) => {
  return xcss({
    position: 'absolute',
    top: `-${NAMEPLATE_HEIGHT_IN_PIXELS - BORDER_WIDTH_IN_PIXELS}px`,
    height: `${NAMEPLATE_HEIGHT_IN_PIXELS}px`,
    width: 'max-content',
    zIndex: 'tooltip',
    borderTopLeftRadius: position === 'top' ? '3px' : '',
    borderTopRightRadius: position === 'top' ? '3px' : '',
    borderBottomLeftRadius: position === 'bottom' ? '3px' : '',
    borderBottomRightRadius: position === 'bottom' ? '3px' : '',
    paddingTop: 'space.025',
    paddingBottom: 'space.025',
    paddingLeft: 'space.075',
    paddingRight: 'space.075',
    color: 'color.text.inverse',
    display: 'flex',
    alignItems: 'center',
  });
};
const Nameplate = ({
  participants,
  position = 'top',
}: {
  participants: Participant[];
  position?: 'top' | 'bottom';
}) => {
  const backgroundColor = participants?.[0]?.colorToken;

  const participantString = useDisplayParticipants(participants);

  return (
    <Box
      xcss={getNameplateStyles({ position })}
      backgroundColor={backgroundColor}
      testId="presence-nameplate"
    >
      <Box xcss={nameStyles}>{participantString}</Box>
    </Box>
  );
};

const nameStyles = xcss({
  minWidth: '0px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  flexShrink: 1,
  flexGrow: 1,
});

export const PresenceNameplate = ({
  participants,
  anchorRef,
  position = 'top',
}: {
  participants: Participant[];
  anchorRef: RefObject<HTMLElement>;
  position?: 'top' | 'bottom';
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const scrollTimeout = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);

  // To avoid a jumpy experience when scrolling, we hide the nameplate while scrolling
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(false);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = globalThis.setTimeout(() => {
        setIsVisible(true);
      }, 150);
    };
    globalThis.addEventListener('scroll', handleScroll, true);
    return () => {
      globalThis.removeEventListener('scroll', handleScroll, true);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  if (!anchorRef.current) {
    return null;
  }
  const rect = anchorRef.current.getBoundingClientRect();
  return (
    isVisible &&
    participants.length > 0 &&
    createPortal(
      <div
        style={{
          position: 'absolute',
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          top:
            position === 'top'
              ? rect.top
              : rect.bottom + NAMEPLATE_HEIGHT_IN_PIXELS - BORDER_WIDTH_IN_PIXELS,
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          left: rect.left + BORDER_WIDTH_IN_PIXELS,
        }}
      >
        <Nameplate participants={participants} position={position} />
      </div>,
      document.body
    )
  );
};
